<template>
  <header class="client-cabinet-header client-cabinet-rental-header" data-testid="client-cabinet-header">
    <locale-select class="client-cabinet-header__locale"></locale-select>
    <div class="client-cabinet-header__brand-row">
      <div class="client-cabinet-header__monogram" aria-hidden="true" data-testid="client-cabinet-monogram">{{ monogram }}</div>
      <div class="client-cabinet-header__brand-copy">
        <h1
          ref="title"
          tabindex="-1"
          class="client-cabinet-header__company"
          data-testid="client-cabinet-company-name"
          data-field="company.name"
        >{{ companyName }}</h1>
        <a
          v-if="company && company.phone"
          :href="phoneHref"
          class="client-cabinet-header__phone"
          data-testid="client-cabinet-company-phone"
          data-field="company.phone"
        >{{ company.phone }}</a>
        <a
          v-if="company && company.email"
          :href="`mailto:${company.email}`"
          class="client-cabinet-header__email"
          data-testid="client-cabinet-company-email"
          data-field="company.email"
        >{{ company.email }}</a>
      </div>
    </div>
    <div class="client-cabinet-header__booking">
      <div
        class="client-cabinet-header__summary"
        data-testid="client-cabinet-summary"
        data-field="booking.car_name booking.start_date booking.end_date booking.days booking.additional_hours booking.hourly booking.hourly_hours booking.weekly booking.weekly_weeks booking.monthly booking.monthly_months"
      >
        <div class="client-cabinet-header__booking-line">
          <span class="client-cabinet-header__number" data-testid="client-cabinet-title" data-field="booking.id">
            {{ $t("client_page.cabinet.title", { id: booking.id }) }}
          </span>
          <span class="client-cabinet-header__state" data-testid="client-cabinet-state" data-field="booking.state">{{ stateText }}</span>
        </div>
        <div v-if="booking.car_name" class="client-cabinet-header__car">{{ booking.car_name }}</div>
        <div v-if="dateSummary" class="client-cabinet-header__dates">{{ dateSummary }}</div>
      </div>
      <p v-if="clientName" class="client-cabinet-header__customer" data-field="client.name client.lastname">{{ clientName }}</p>
    </div>
  </header>
</template>

<script>
import { personName } from "@/utils/personName";
// FT-200 STEP-05 (REQ-08, U4): шапка кабинета — h1 с названием и телефоном проката, затем номер, статус и
// строка «машина · даты · срок».
import moment from "moment";
import { momentLocaleFor, translateBookingState } from "@/utils/localeInfra";
import LocaleSelect from "@/components/common/LocaleSelect.vue";
import { telHref } from "@/lib/phone/display";

export default {
  name: "CabinetHeader",
  components: { LocaleSelect },
  props: {
    booking: { type: Object, required: true },
    company: { type: Object, default: null },
    client: { type: Object, default: null },
  },
  computed: {
    companyName() {
      return (this.company && this.company.name) || this.$t("client_page.login.title_neutral");
    },
    monogram() {
      const words = this.companyName.trim().split(/\s+/).filter(Boolean);
      return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0].slice(0, 2)).toLocaleUpperCase();
    },
    phoneHref() {
      return telHref(this.company.phone);
    },
    stateText() {
      return this.booking.state ? translateBookingState((key) => this.$t(key), this.booking.state) : "";
    },
    // Срок — по единице аренды бронирования.
    duration() {
      const b = this.booking;
      if (b.hourly && b.hourly_hours) return this.$t("client_page.cabinet.duration_hours", { count: b.hourly_hours });
      if (b.weekly && b.weekly_weeks) return this.$t("client_page.cabinet.duration_weeks", { count: b.weekly_weeks });
      if (b.monthly && b.monthly_months) return this.$t("client_page.cabinet.duration_months", { count: b.monthly_months });
      if (!b.days) return "";
      const days = this.$t("client_page.cabinet.duration_days", { count: b.days });
      return b.additional_hours > 0
        ? `${days} ${this.$t("client_page.cabinet.duration_extra_hours", { count: b.additional_hours })}`
        : days;
    },
    dateRange() {
      const start = this.wallDate(this.booking.start_date);
      const end = this.wallDate(this.booking.end_date);
      if (!start || !end) return [this.booking.start_date, this.booking.end_date].filter(Boolean).join(" — ");
      if (start.year() === end.year() && start.month() === end.month()) return `${start.format("D")}–${end.format("D MMMM")}`;
      return `${start.format("D MMMM")}–${end.format("D MMMM")}`;
    },
    dateSummary() {
      return [this.dateRange, this.duration].filter(Boolean).join(" · ");
    },
    clientName() {
      if (!this.client) return "";
      return personName(this.client.name, this.client.lastname);
    },
  },
  methods: {
    wallDate(value) {
      if (!value) return null;
      const date = moment.utc(value, "DD-MM-YYYY HH:mm", true);
      return date.isValid() ? date.locale(momentLocaleFor((this.$i18n && this.$i18n.locale) || "ru")) : null;
    },
    // REQ-13: после входа фокус — на заголовок кабинета (ClientPage.onLoggedIn).
    focusTitle() {
      const title = this.$refs.title;
      if (title && typeof title.focus === "function") title.focus();
    },
  },
};
</script>

<style scoped>
/* Заголовок получает фокус программно (после входа — для экранного диктора), он не интерактивный:
   кольцо фокуса на нём не нужно, иначе при загрузке страницы название обведено рамкой. */
.client-cabinet-header__company:focus {
  outline: none;
}
.client-cabinet-header__locale {
  position: relative;
  z-index: 2;
  display: block;
  margin: 0 0 10px auto;
}

.client-cabinet-header__phone:focus-visible,
.client-cabinet-header__email:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
